import Big from 'big.js';
import { useCallback, useContext, useMemo } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { dateToPrint } from '@/modules/common/utils/date-format.ts';
import { formatNumberWithCommas } from '@/modules/common/utils/format-numbers.ts';
import { IssuerOperationArchiveButton } from '@/modules/issuer-dashboard/components/IssuerOperationArchiveButton.tsx';
import {
  IssuerConfirmTransferModal,
  IssuerConfirmTransferModalProps,
} from '@/modules/issuer-dashboard/components/modals/IssuerConfirmTransferModal.tsx';
import { IssuerTransferOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerTransfersOperationInfoModal.tsx';
import {
  useTransferArchiveByIdMutation,
  useTransferSetRejectedMutation,
} from '@/modules/issuer-dashboard/queries/transfers.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  Button,
  DeclineModal,
  DeclineModalProps,
  DisclosureListItem,
  ModalProviderContext,
} from 'ui';
import { fixNameEnum, goodFormatUSDCurrency, goodFormatUSDCurrencyLowPrecision } from 'ui/utils';
import { TransactionStatus, TransferPaginateResponse, TransferSetRejectedStatusRequest } from 'api';

interface IssuerTransferItemProps extends TransferPaginateResponse {
  isDeleted?: boolean;
  showArchiveButton?: boolean;
}

export const IssuerTransferItem = ({
  id,
  memo,
  status,
  quantity,
  register_obj,
  register_class,
  price_per_share,
  requested_at_date,
  confirmation_date,
  to_shareholder_user,
  from_shareholder_user,
  to_shareholder_entity,
  from_shareholder_entity,
  isDeleted = false,
  showArchiveButton = false,
  ...transfer
}: IssuerTransferItemProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const [params] = useSearchParams();
  const { pathname } = useLocation();

  const { mutateAsync: rejectTransfer } = useTransferSetRejectedMutation();
  const { mutateAsync: archiveTransfer } = useTransferArchiveByIdMutation();

  const handleRejectTransfer = useCallback(
    async (payload: TransferSetRejectedStatusRequest) => {
      await rejectTransfer({ ...payload, id });
    },
    [id, rejectTransfer],
  );

  const transactionStatusPendingActions = useMemo(
    () =>
      status === TransactionStatus.Pending ? (
        <div className="col-span-full min-w-[220px] flex items-center justify-end space-x-4">
          <Button
            className="w-[150px]"
            variant="secondary"
            onClick={() =>
              openModal<DeclineModalProps<TransferSetRejectedStatusRequest>>(DeclineModal, {
                onConfirm: handleRejectTransfer,
              })
            }
          >
            Decline
          </Button>
          <Button
            className="w-[150px]"
            onClick={() =>
              openModal<IssuerConfirmTransferModalProps>(IssuerConfirmTransferModal, {
                id,
                status,
                requested_at_date,
                register_obj,
                memo,
                from_shareholder_user,
                to_shareholder_user,
                register_class,
                quantity,
                price_per_share,
                ...transfer,
              })
            }
          >
            Confirm
          </Button>
        </div>
      ) : null,
    [
      from_shareholder_user,
      handleRejectTransfer,
      id,
      memo,
      openModal,
      price_per_share,
      quantity,
      register_class,
      register_obj,
      requested_at_date,
      status,
      to_shareholder_user,
      transfer,
    ],
  );

  const handleOpenFullTransfer = useCallback(() => {
    openModal(IssuerTransferOperationInfoModal, {
      id,
      isDeleted,
    });
  }, [isDeleted, id, openModal]);

  const bottomContentData = useMemo<Array<[JSX.Element | string, JSX.Element | string]>>(() => {
    const info: Array<[JSX.Element | string, JSX.Element | string]> = [
      [
        'Transfer ID',
        <span
          className="text-sm leading-[22px] text-[#0369A1] font-[500] cursor-pointer hover:underline"
          onClick={handleOpenFullTransfer}
        >
          #{id}
        </span>,
      ],
      ['Memo', memo || '-'],
    ];

    if (!isDeleted && !!transactionStatusPendingActions) {
      info.push([transactionStatusPendingActions, <></>]);
    }

    if (!isDeleted && showArchiveButton) {
      info.push([
        <IssuerOperationArchiveButton id={id} entityName="transfer" onArchive={archiveTransfer} />,
        <></>,
      ]);
    }

    if (price_per_share) {
      info.splice(1, 0, [
        'Price per share/unit',
        goodFormatUSDCurrency(price_per_share).toString(),
      ]);
    }

    return info;
  }, [
    isDeleted,
    handleOpenFullTransfer,
    id,
    memo,
    price_per_share,
    transactionStatusPendingActions,
    showArchiveButton,
    archiveTransfer,
  ]);

  return (
    <DisclosureListItem
      leftMainString={
        <p className="flex gap-1 flex-wrap">
          <span className="text-nowrap">
            From{' '}
            <Link
              state={{ from: pathname + '?' + params.toString() }}
              to={{
                pathname: issuerDashboardPaths.dynamic.holder(
                  from_shareholder_entity?.id?.toString(),
                ),
                search: params.toString(),
              }}
              className="text-blue-700 capitalize hover:underline"
            >
              {from_shareholder_entity?.entity_name}
            </Link>
          </span>

          <span className="text-nowrap">
            to{' '}
            <Link
              state={{ from: pathname + '?' + params.toString() }}
              to={{
                pathname: issuerDashboardPaths.dynamic.holder(
                  to_shareholder_entity?.id?.toString(),
                ),
                search: params.toString(),
              }}
              className="text-blue-700 capitalize hover:underline"
            >
              {to_shareholder_entity?.entity_name}
            </Link>
          </span>
        </p>
      }
      leftSubduedContentSet={[
        dateToPrint(confirmation_date || requested_at_date) || '-',
        `${fixNameEnum(register_obj.name)} ${register_class.class_name}`,
      ]}
      rightContent={<ActivityStatusBadge status={status || ''} />}
      classes={{ rightMainString: 'text-nowrap text-right', rightContentUp: 'justify-items-end' }}
      rightMainString={goodFormatUSDCurrencyLowPrecision(
        +new Big(quantity)?.mul(price_per_share || 0)?.valueOf(),
      )}
      rightSubduedString={`${formatNumberWithCommas(quantity)} units/shares`}
      bottomContent={bottomContentData}
    />
  );
};
